import React, { ReactNode } from "react";

import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { AxiosError } from "axios";

import { DEFAULT_CACHE_TIME } from "consts";

export const persistentQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        if (error instanceof AxiosError && error.response?.status === 418) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: DEFAULT_CACHE_TIME,
      gcTime: DEFAULT_CACHE_TIME,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
});

/**
 * @function clearPersistedQueryCache
 * @description Tears down both halves of the query cache when a session ends or a new one begins:
 * the in-memory queries and the copy the persister has written to localStorage. Removing the
 * queries alone is not enough, because the persister throttles its writes and can leave the
 * previous session's serialised cache on disk. Await this before navigating, so the next user
 * never sees the previous user's data.
 * @returns {Promise<void>} Resolves once the in-memory queries are dropped and the persisted client is removed.
 */
export const clearPersistedQueryCache = async (): Promise<void> => {
  persistentQueryClient.removeQueries();
  await persister.removeClient();
};

export const PersistentCacheContext = ({ children }: { children: ReactNode }) => (
  <PersistQueryClientProvider
    client={persistentQueryClient}
    persistOptions={{
      buster: "1791807846486", // 2026-10-12, 14:24, CarbonProgram shape change
      persister,
    }}
  >
    {children}
  </PersistQueryClientProvider>
);

export const TempCacheContext = ({ children }: { children: ReactNode }) => <QueryClientProvider client={persistentQueryClient}>{children}</QueryClientProvider>;
